import {
  renderAnnotationRepairTray,
  withOrderNormalizationReport,
  type AnnotationRepairReport,
  type AnnotationRepairTray,
} from "./annotation-repair-tray.js";
import {
  projectAnnotations,
  repairAnnotationRanges,
  type AnnotationProjection,
  type AnnotationRange,
} from "./annotations.js";
import {
  CHOREOGRAPHY_DEFINITIONS,
  THEME_DEFINITIONS,
} from "./atlas.js";
import { EDITOR_MODES, type EditorMode } from "./pack.js";
import { mountRendererHost, type RendererHost } from "./renderer-host.js";
import { resolveScenePlan, type ScenePlan } from "./scene-plan.js";
import {
  validateEditorResumability,
  validateSessionRecord,
  type NorthStarSessionContents,
  type SessionJsonObject,
  type SessionSaveResult,
} from "./session.js";
import { createStageRendererFactories } from "./stage-renderer-factories.js";

export { EDITOR_MODES } from "./pack.js";

type ThemeId = (typeof THEME_DEFINITIONS)[number]["id"];
type ChoreographyId = (typeof CHOREOGRAPHY_DEFINITIONS)[number]["id"];

export interface EditorSelectionOffsets {
  readonly start: number;
  readonly end: number;
}

export interface EditorSelectionRange {
  readonly startContainer: Node;
  readonly startOffset: number;
  readonly endContainer: Node;
  readonly endOffset: number;
}

export interface EditorSelection {
  readonly rangeCount: number;
  getRangeAt(index: number): EditorSelectionRange;
}

export interface CanonicalEditorState {
  readonly packRef: string;
  readonly lines: readonly [string, string, string];
  readonly mode: EditorMode;
  readonly themeId: ThemeId;
  readonly choreographyId: ChoreographyId;
  readonly annotations: readonly AnnotationRange[];
  readonly games: SessionJsonObject;
  readonly repairPending: boolean;
}

export interface NorthStarEditorInitialState {
  readonly packRef: string;
  readonly lines: readonly [string, string, string];
  readonly mode: EditorMode;
  readonly themeId: ThemeId;
  readonly choreographyId: ChoreographyId;
  readonly annotations: readonly AnnotationRange[];
  readonly games: SessionJsonObject;
}

export interface EditorAvailability {
  readonly themeIds: readonly ThemeId[];
  readonly choreographyIds: readonly ChoreographyId[];
}

export interface EditorSessionBoundary {
  save(contents: NorthStarSessionContents): SessionSaveResult;
}

export interface EditorRendererBoundary {
  mount(root: HTMLElement, scenePlan: ScenePlan): Pick<RendererHost, "destroy">;
}

export interface CreateNorthStarEditorOptions {
  readonly root: HTMLElement;
  readonly initial: NorthStarEditorInitialState;
  readonly availability: EditorAvailability;
  readonly session: EditorSessionBoundary;
  readonly navigation?: "embedded" | "external";
  readonly onModeChanged?: (mode: EditorMode) => void;
  readonly renderer?: EditorRendererBoundary;
  readonly getSelection?: () => EditorSelection | null;
}

export type AnnotationCaptureResult =
  | { readonly status: "accepted"; readonly range: AnnotationRange }
  | { readonly status: "invalid-selection" }
  | { readonly status: "repair-pending"; readonly range: AnnotationRange };

export interface NorthStarEditorController {
  readonly element: HTMLElement;
  readonly mode: EditorMode;
  readonly state: CanonicalEditorState;
  readonly canonicalText: string;
  readonly scenePlan: ScenePlan | null;
  setMode(mode: EditorMode): boolean;
  captureSelection(id: string, selection?: EditorSelection | null): AnnotationCaptureResult;
  destroy(): void;
}

interface MutableEditorState {
  readonly packRef: string;
  lines: [string, string, string];
  mode: EditorMode;
  themeId: ThemeId;
  choreographyId: ChoreographyId;
  annotations: readonly AnnotationRange[];
  readonly games: SessionJsonObject;
  repairPending: boolean;
}

interface AnnotationStatus {
  readonly pending: boolean;
  readonly result: AnnotationRepairReport | null;
}

interface ModeOwnership {
  readonly mode: EditorMode;
  readonly element: HTMLElement;
  readonly cleanups: ListenerCleanup[];
  fallbackFocus: string;
  annotationElement: HTMLElement | null;
  projection: AnnotationProjection | null;
  renderer: Pick<RendererHost, "destroy"> | null;
  scenePlan: ScenePlan | null;
  destroyed: boolean;
}

interface TrayOwnership {
  readonly host: HTMLElement;
  tray: AnnotationRepairTray | null;
  destroyed: boolean;
}

type ListenerCleanup = () => void;

const isHighSurrogate = (value: number): boolean => value >= 0xd800 && value <= 0xdbff;
const isLowSurrogate = (value: number): boolean => value >= 0xdc00 && value <= 0xdfff;

const boundaryUtf16Offset = (
  root: HTMLElement,
  boundaryNode: Node,
  boundaryOffset: number,
): number | null => {
  if (!Number.isInteger(boundaryOffset) || boundaryOffset < 0 || !root.contains(boundaryNode)) {
    return null;
  }

  let localOffset = 0;
  if (boundaryNode.nodeType === 3) {
    const text = boundaryNode.textContent ?? "";
    if (boundaryOffset > text.length) return null;
    localOffset = boundaryOffset;
  } else if (boundaryNode.nodeType === 1) {
    const children = Array.from(boundaryNode.childNodes);
    if (boundaryOffset > children.length) return null;
    localOffset = children
      .slice(0, boundaryOffset)
      .reduce((length, child) => length + (child.textContent?.length ?? 0), 0);
  } else {
    return null;
  }

  let current: Node = boundaryNode;
  let offset = localOffset;
  while (current !== root) {
    const parent = current.parentNode;
    if (parent === null) return null;
    const siblings = Array.from(parent.childNodes);
    const index = siblings.indexOf(current as ChildNode);
    if (index < 0) return null;
    offset += siblings
      .slice(0, index)
      .reduce((length, sibling) => length + (sibling.textContent?.length ?? 0), 0);
    current = parent;
  }
  return offset;
};

/** Converts valid DOM range boundaries to offsets in Unicode code points. */
export const selectionToCodePointOffsets = (
  root: HTMLElement,
  selection: EditorSelection | null,
): EditorSelectionOffsets | null => {
  if (selection === null || selection.rangeCount !== 1) return null;
  let range: EditorSelectionRange;
  try {
    range = selection.getRangeAt(0);
  } catch {
    return null;
  }

  const startUtf16 = boundaryUtf16Offset(root, range.startContainer, range.startOffset);
  const endUtf16 = boundaryUtf16Offset(root, range.endContainer, range.endOffset);
  if (startUtf16 === null || endUtf16 === null || startUtf16 >= endUtf16) return null;

  const canonicalText = root.textContent;
  if (endUtf16 > canonicalText.length) return null;
  const splitsSurrogatePair = (offset: number): boolean =>
    offset > 0
    && offset < canonicalText.length
    && isHighSurrogate(canonicalText.charCodeAt(offset - 1))
    && isLowSurrogate(canonicalText.charCodeAt(offset));
  if (splitsSurrogatePair(startUtf16) || splitsSurrogatePair(endUtf16)) return null;

  return Object.freeze({
    start: Array.from(canonicalText.slice(0, startUtf16)).length,
    end: Array.from(canonicalText.slice(0, endUtf16)).length,
  });
};

const freezeRange = (range: AnnotationRange): AnnotationRange => Object.freeze({
  id: range.id,
  start: range.start,
  end: range.end,
});

const freezeRanges = (ranges: readonly AnnotationRange[]): readonly AnnotationRange[] =>
  Object.freeze(ranges.map(freezeRange));

const rangesMatch = (
  left: readonly AnnotationRange[],
  right: readonly AnnotationRange[],
): boolean => left.length === right.length && left.every((range, index) => {
  const candidate = right[index];
  return candidate !== undefined
    && range.id === candidate.id
    && range.start === candidate.start
    && range.end === candidate.end;
});

interface EditorInitialSnapshot {
  readonly packRef: string;
  readonly lines: readonly [string, string, string];
  readonly mode: EditorMode;
  readonly themeId: ThemeId;
  readonly choreographyId: ChoreographyId;
  readonly annotations: readonly AnnotationRange[];
  readonly games: SessionJsonObject;
}

const INITIAL_KEYS = Object.freeze([
  "packRef",
  "lines",
  "mode",
  "themeId",
  "choreographyId",
  "annotations",
  "games",
] as const);

const readExactDataRecord = (
  value: unknown,
  keys: readonly string[],
  label: string,
): readonly unknown[] => {
  try {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      throw new TypeError(`${label} must be a plain object`);
    }
    const prototype: unknown = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      throw new TypeError(`${label} must be a plain object`);
    }
    const actualKeys = Reflect.ownKeys(value);
    if (actualKeys.length !== keys.length
        || actualKeys.some((key) => typeof key !== "string" || !keys.includes(key))) {
      throw new TypeError(`${label} contains unknown or missing keys`);
    }
    return keys.map((key) => {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
        throw new TypeError(`${label}.${key} must be an enumerable data property`);
      }
      return descriptor.value as unknown;
    });
  } catch (error) {
    if (error instanceof TypeError) throw error;
    throw new TypeError(`${label} must expose safe data properties`, { cause: error });
  }
};

const snapshotInitialAnnotations = (value: unknown): readonly AnnotationRange[] => {
  try {
    if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) {
      throw new TypeError("editor initial annotations must be a plain array");
    }
    const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
    if (lengthDescriptor === undefined || !("value" in lengthDescriptor)
        || !Number.isSafeInteger(lengthDescriptor.value) || lengthDescriptor.value < 0) {
      throw new TypeError("editor initial annotations require a safe length");
    }
    const length = lengthDescriptor.value as number;
    if (Reflect.ownKeys(value).length !== length + 1) {
      throw new TypeError("editor initial annotations must be dense without extra properties");
    }
    const snapshot: AnnotationRange[] = [];
    for (let index = 0; index < length; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
        throw new TypeError(`editor initial annotations[${index}] must be a data property`);
      }
      const [id, start, end] = readExactDataRecord(
        descriptor.value,
        ["id", "start", "end"],
        `editor initial annotations[${index}]`,
      );
      if (typeof id !== "string" || id.length === 0
          || typeof start !== "number" || typeof end !== "number") {
        throw new TypeError(`editor initial annotations[${index}] is invalid`);
      }
      snapshot.push(freezeRange({ id, start, end }));
    }
    return Object.freeze(snapshot);
  } catch (error) {
    if (error instanceof TypeError) throw error;
    throw new TypeError("editor initial annotations must be safe data", { cause: error });
  }
};

const snapshotInitialState = (initial: NorthStarEditorInitialState): EditorInitialSnapshot => {
  const [packRef, lines, mode, themeId, choreographyId, annotations, games] =
    readExactDataRecord(initial, INITIAL_KEYS, "editor initial state");
  const resumability = validateEditorResumability(
    lines,
    themeId,
    choreographyId,
  );
  const annotationSnapshot = snapshotInitialAnnotations(annotations);
  const validated = validateSessionRecord({
    schemaVersion: 1,
    packRef,
    draft: resumability.lines.join("\n"),
    editorMode: mode,
    themeId: resumability.themeId,
    choreographyId: resumability.choreographyId,
    annotations: [],
    games,
    updatedAt: 0,
  });
  try {
    globalThis.structuredClone(initial);
  } catch (error) {
    throw new TypeError("editor initial state must not contain proxies", { cause: error });
  }
  return Object.freeze({
    packRef: validated.packRef,
    lines: resumability.lines,
    mode: validated.editorMode,
    themeId: resumability.themeId,
    choreographyId: resumability.choreographyId,
    annotations: annotationSnapshot,
    games: validated.games,
  });
};

const defaultRendererBoundary: EditorRendererBoundary = Object.freeze({
  mount(root: HTMLElement, scenePlan: ScenePlan): RendererHost {
    return mountRendererHost({
      root,
      scenePlan,
      factories: createStageRendererFactories(),
    });
  },
});

const appendTextElement = (
  document: Document,
  parent: HTMLElement,
  tagName: string,
  text: string,
): HTMLElement => {
  const element = document.createElement(tagName);
  element.textContent = text;
  parent.append(element);
  return element;
};

const throwCleanupErrors = (errors: readonly unknown[], message: string): void => {
  if (errors.length > 0) throw new AggregateError(errors, message);
};

let editorInstanceCounter = 0;

export const createNorthStarEditor = (
  options: CreateNorthStarEditorOptions,
): NorthStarEditorController => {
  const initial = snapshotInitialState(options.initial);

  const themeAvailability = new Set(options.availability.themeIds);
  const choreographyAvailability = new Set(options.availability.choreographyIds);
  if (!themeAvailability.has(initial.themeId)
      || !choreographyAvailability.has(initial.choreographyId)) {
    throw new RangeError("initial Weave selection must be atlas-available");
  }

  const document = options.root.ownerDocument;
  const navigation = options.navigation ?? "embedded";
  const rendererBoundary = options.renderer ?? defaultRendererBoundary;
  const state: MutableEditorState = {
    packRef: initial.packRef,
    lines: [initial.lines[0], initial.lines[1], initial.lines[2]],
    mode: initial.mode,
    themeId: initial.themeId,
    choreographyId: initial.choreographyId,
    annotations: initial.annotations,
    games: initial.games,
    repairPending: false,
  };

  const owned = document.createElement("section");
  owned.setAttribute("data-north-star-editor", "");
  const modeNavigation = navigation === "embedded" ? document.createElement("nav") : null;
  modeNavigation?.setAttribute("aria-label", "Editor modes");
  const pendingSlot = document.createElement("section");
  const modeSlot = document.createElement("section");
  modeSlot.setAttribute("data-editor-panel-slot", "");
  if (modeNavigation !== null) owned.append(modeNavigation);
  owned.append(pendingSlot, modeSlot);

  const instanceId = ++editorInstanceCounter;
  const globalCleanups: ListenerCleanup[] = [];
  const focusByMode = new Map<EditorMode, string>();
  const modeButtons = new Map<EditorMode, HTMLButtonElement>();
  let currentOwnership: ModeOwnership | null = null;
  let trayOwnership: TrayOwnership | null = null;
  let provisionalModeOwnership: ModeOwnership | null = null;
  let provisionalTrayOwnership: TrayOwnership | null = null;
  let modeGeneration = 0;
  let stateGeneration = 0;
  let transactionActive = false;
  let destroyed = false;

  const beginTransaction = (): boolean => {
    if (destroyed || transactionActive) {
      return false;
    }
    transactionActive = true;
    return true;
  };

  const endTransaction = (): void => {
    transactionActive = false;
  };

  const canMutateOwnership = (ownership: ModeOwnership): boolean =>
    !destroyed &&
    !transactionActive &&
    currentOwnership === ownership &&
    !ownership.destroyed;

  const listen = (
    target: EventTarget,
    type: string,
    listener: EventListener,
    cleanups: ListenerCleanup[],
  ): void => {
    target.addEventListener(type, listener);
    cleanups.push((): void => target.removeEventListener(type, listener));
  };

  const canonicalText = (): string => state.lines.join("\n");

  const annotationStatus = (
    annotations: readonly AnnotationRange[],
    text = canonicalText(),
  ): AnnotationStatus => {
    const deterministic = repairAnnotationRanges(text, annotations);
    if (rangesMatch(annotations, deterministic.accepted)) {
      return Object.freeze({ pending: false, result: null });
    }
    return Object.freeze({
      pending: true,
      result: withOrderNormalizationReport(annotations, deterministic),
    });
  };

  const snapshot = (): CanonicalEditorState => Object.freeze({
    packRef: state.packRef,
    lines: Object.freeze(
      [state.lines[0], state.lines[1], state.lines[2]] satisfies [string, string, string],
    ),
    mode: state.mode,
    themeId: state.themeId,
    choreographyId: state.choreographyId,
    annotations: freezeRanges(state.annotations),
    games: state.games,
    repairPending: state.repairPending,
  });

  const saveContents = (targetMode: EditorMode): NorthStarSessionContents => Object.freeze({
    packRef: state.packRef,
    draft: canonicalText(),
    editorMode: targetMode,
    themeId: state.themeId,
    choreographyId: state.choreographyId,
    annotations: freezeRanges(state.annotations),
    games: state.games,
  });

  const cleanupMode = (ownership: ModeOwnership | null): unknown[] => {
    if (provisionalModeOwnership === ownership) provisionalModeOwnership = null;
    if (ownership === null || ownership.destroyed) return [];
    ownership.destroyed = true;
    const projection = ownership.projection;
    const renderer = ownership.renderer;
    const cleanups = ownership.cleanups.splice(0);
    ownership.projection = null;
    ownership.renderer = null;
    ownership.annotationElement = null;
    ownership.scenePlan = null;
    const errors: unknown[] = [];
    try { projection?.destroy(); } catch (error) { errors.push(error); }
    try { renderer?.destroy(); } catch (error) { errors.push(error); }
    for (const cleanup of cleanups) {
      try { cleanup(); } catch (error) { errors.push(error); }
    }
    try { ownership.element.remove(); } catch (error) { errors.push(error); }
    return errors;
  };

  const cleanupTray = (ownership: TrayOwnership | null): unknown[] => {
    if (provisionalTrayOwnership === ownership) provisionalTrayOwnership = null;
    if (ownership === null || ownership.destroyed) return [];
    ownership.destroyed = true;
    const tray = ownership.tray;
    ownership.tray = null;
    const errors: unknown[] = [];
    try { tray?.destroy(); } catch (error) { errors.push(error); }
    try { ownership.host.remove(); } catch (error) { errors.push(error); }
    return errors;
  };

  const provisionallyAttach = (
    entries: readonly { readonly slot: HTMLElement; readonly node: HTMLElement }[],
  ): void => {
    const attempted: HTMLElement[] = [];
    try {
      for (const { slot, node } of entries) {
        attempted.push(node);
        node.hidden = true;
        node.inert = true;
        node.setAttribute("aria-hidden", "true");
        node.setAttribute("inert", "");
        slot.append(node);
      }
    } catch (error) {
      const errors: unknown[] = [error];
      for (const node of attempted.reverse()) {
        try { node.remove(); } catch (cleanupError) { errors.push(cleanupError); }
      }
      if (errors.length > 1) {
        throw new AggregateError(errors, "provisional editor attachment failed");
      }
      throw error;
    }
  };

  const revealProvisional = (node: HTMLElement): void => {
    try { node.hidden = false; } catch { /* best-effort */ }
    try { node.inert = false; } catch { /* best-effort */ }
    try { node.removeAttribute("aria-hidden"); } catch { /* best-effort */ }
    try { node.removeAttribute("inert"); } catch { /* best-effort */ }
  };

  const bestEffortFocus = (ownership: ModeOwnership): void => {
    const key = focusByMode.get(ownership.mode) ?? ownership.fallbackFocus;
    let target: HTMLElement | null = null;
    try {
      for (const candidate of ownership.element.querySelectorAll<HTMLElement>("[data-editor-focus]")) {
        if (candidate.getAttribute("data-editor-focus") === key) {
          target = candidate;
          break;
        }
      }
      if (target === null) {
        for (const candidate of ownership.element.querySelectorAll<HTMLElement>("[data-editor-focus]")) {
          if (candidate.getAttribute("data-editor-focus") === ownership.fallbackFocus) {
            target = candidate;
            break;
          }
        }
      }
      target?.focus({ preventScroll: true });
    } catch {
      // Focus restoration is best-effort and never owns transaction success.
    }
  };

  const updateModeButtons = (): void => {
    for (const [mode, button] of modeButtons) {
      try { button.setAttribute("aria-pressed", String(mode === state.mode)); } catch { /* best-effort */ }
      try {
        if (mode === state.mode) button.setAttribute("aria-current", "true");
        else button.removeAttribute("aria-current");
      } catch { /* best-effort */ }
      try {
        if (currentOwnership !== null) {
          button.setAttribute("aria-controls", currentOwnership.element.id);
        }
      } catch { /* best-effort */ }
    }
  };

  function prepareTray(result: AnnotationRepairReport): TrayOwnership {
    const host = document.createElement("section");
    host.setAttribute("data-editor-repair-tray", "");
    const ownership: TrayOwnership = { host, tray: null, destroyed: false };
    if (provisionalTrayOwnership !== null) {
      throw new Error("another repair tray is already provisional");
    }
    provisionalTrayOwnership = ownership;
    try {
      const tray = renderAnnotationRepairTray(host, result, {
        onUse(accepted): void { commitRepairDecision(accepted); },
        onDiscard(retained): void { commitRepairDecision(retained); },
      });
      if (tray === null) throw new Error("pending annotation result did not create a repair tray");
      if (destroyed || provisionalTrayOwnership !== ownership || ownership.destroyed) {
        const errors: unknown[] = [];
        try { tray.destroy(); } catch (cleanupError) { errors.push(cleanupError); }
        if (errors.length > 0) {
          throw new AggregateError(errors, "retired repair tray cleanup failed");
        }
        throw new Error("repair tray preparation was retired");
      }
      ownership.tray = tray;
      return ownership;
    } catch (error) {
      const cleanupErrors = cleanupTray(ownership);
      if (cleanupErrors.length > 0) {
        throw new AggregateError([error, ...cleanupErrors], "repair tray preparation failed");
      }
      throw error;
    }
  }

  const commitModeOwnership = (
    candidate: ModeOwnership,
    mode: EditorMode,
    focus: boolean,
  ): void => {
    const old = currentOwnership;
    if (provisionalModeOwnership === candidate) provisionalModeOwnership = null;
    currentOwnership = candidate;
    state.mode = mode;
    stateGeneration += 1;
    revealProvisional(candidate.element);
    updateModeButtons();
    cleanupMode(old);
    if (focus) bestEffortFocus(candidate);
  };

  function prepareMode(
    mode: EditorMode,
    annotations: readonly AnnotationRange[],
    repair: AnnotationRepairReport | null,
  ): ModeOwnership {
    const element = document.createElement("section");
    element.setAttribute("data-editor-panel", "");
    element.setAttribute("data-editor-current-mode", mode);
    modeGeneration += 1;
    element.id = `north-star-editor-${instanceId}-panel-${modeGeneration}`;
    const ownership: ModeOwnership = {
      mode,
      element,
      cleanups: [],
      fallbackFocus: "",
      annotationElement: null,
      projection: null,
      renderer: null,
      scenePlan: null,
      destroyed: false,
    };
    if (provisionalModeOwnership !== null) {
      throw new Error("another editor mode is already provisional");
    }
    provisionalModeOwnership = ownership;

    const trackFocus = (target: HTMLElement, key: string): void => {
      target.setAttribute("data-editor-focus", key);
      listen(target, "focus", (): void => {
        if (!canMutateOwnership(ownership) || !beginTransaction()) return;
        try {
          if (currentOwnership !== ownership || destroyed) return;
          focusByMode.set(mode, key);
          stateGeneration += 1;
        } finally {
          endTransaction();
        }
      }, ownership.cleanups);
    };

    const canonicalPoem = (key: string): HTMLElement => {
      const poem = document.createElement("article");
      poem.setAttribute("data-editor-canonical-poem", "");
      poem.setAttribute("aria-label", "Canonical three-line haiku");
      poem.tabIndex = 0;
      poem.textContent = canonicalText();
      trackFocus(poem, key);
      return poem;
    };

    const selectRadio = <Id extends string>(
      definitions: readonly { readonly id: Id }[],
      available: ReadonlySet<Id>,
      radios: readonly HTMLInputElement[],
      id: Id,
      selected: () => Id,
      apply: (selected: Id) => void,
    ): void => {
      const sync = (): void => radios.forEach((radio, index): void => {
        radio.checked = definitions[index]?.id === id;
        radio.tabIndex = radio.checked ? 0 : -1;
      });
      const restore = (): void => radios.forEach((radio, index): void => {
        radio.checked = definitions[index]?.id === selected();
        radio.tabIndex = radio.checked ? 0 : -1;
      });
      if (!available.has(id) || !canMutateOwnership(ownership) || !beginTransaction()) {
        restore();
        return;
      }
      try {
        if (currentOwnership !== ownership || destroyed) {
          restore();
          return;
        }
        if (selected() !== id) {
          apply(id);
          stateGeneration += 1;
        }
        sync();
        const index = definitions.findIndex((definition) => definition.id === id);
        const target = radios[index];
        const focusKey = target?.getAttribute("data-editor-focus");
        if (focusKey !== null && focusKey !== undefined) focusByMode.set(mode, focusKey);
        try { target?.focus({ preventScroll: true }); } catch { /* best-effort */ }
      } finally {
        endTransaction();
      }
    };

    const radioGroup = <Id extends string>(
      label: string,
      attribute: string,
      focusPrefix: string,
      definitions: readonly { readonly id: Id }[],
      available: ReadonlySet<Id>,
      selected: () => Id,
      apply: (id: Id) => void,
    ): void => {
      const fieldset = document.createElement("fieldset");
      appendTextElement(document, fieldset, "legend", label);
      const radios: HTMLInputElement[] = [];
      const availableIds = definitions.flatMap(({ id }) => available.has(id) ? [id] : []);
      if (availableIds.length === 0) throw new RangeError(`${label} has no atlas-available options`);
      definitions.forEach(({ id }, index): void => {
        const inputId = `north-star-editor-${instanceId}-${focusPrefix}-${index + 1}`;
        const radio = document.createElement("input");
        radio.id = inputId;
        radio.type = "radio";
        radio.name = `north-star-editor-${instanceId}-${focusPrefix}`;
        radio.value = id;
        radio.checked = id === selected();
        radio.disabled = !available.has(id);
        radio.tabIndex = radio.checked ? 0 : -1;
        radio.setAttribute(attribute, id);
        trackFocus(radio, `${focusPrefix}-${id}`);
        const optionLabel = appendTextElement(document, fieldset, "label", id);
        optionLabel.setAttribute("for", inputId);
        listen(radio, "change", (): void => {
          if (radio.checked && !radio.disabled) {
            selectRadio(definitions, available, radios, id, selected, apply);
          }
          else radios.forEach((candidate, candidateIndex): void => {
            candidate.checked = definitions[candidateIndex]?.id === selected();
          });
        }, ownership.cleanups);
        listen(radio, "click", (): void => {
          if (!radio.disabled) selectRadio(definitions, available, radios, id, selected, apply);
        }, ownership.cleanups);
        listen(radio, "keydown", ((event: KeyboardEvent): void => {
          const keys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
          if (!keys.includes(event.key)) return;
          event.preventDefault();
          const currentIndex = Math.max(0, availableIds.indexOf(selected()));
          let nextIndex = currentIndex;
          if (event.key === "Home") nextIndex = 0;
          else if (event.key === "End") nextIndex = availableIds.length - 1;
          else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            nextIndex = (currentIndex + 1) % availableIds.length;
          } else {
            nextIndex = (currentIndex - 1 + availableIds.length) % availableIds.length;
          }
          const next = availableIds[nextIndex];
          if (next !== undefined) {
            selectRadio(definitions, available, radios, next, selected, apply);
          }
        }) as EventListener, ownership.cleanups);
        fieldset.append(radio);
        radios.push(radio);
      });
      element.append(fieldset);
    };

    try {
      if (mode === "compose") {
        const fieldset = document.createElement("fieldset");
        appendTextElement(document, fieldset, "legend", "Compose haiku");
        state.lines.forEach((line, index): void => {
          const id = `north-star-editor-${instanceId}-line-${index + 1}`;
          const label = appendTextElement(document, fieldset, "label", `Line ${index + 1}`);
          label.setAttribute("for", id);
          const input = document.createElement("input");
          input.id = id;
          input.type = "text";
          input.value = line;
          input.setAttribute("data-editor-line", String(index + 1));
          input.setAttribute("aria-label", `Haiku line ${index + 1}`);
          trackFocus(input, `compose-line-${index + 1}`);
          listen(input, "input", (): void => {
            if (!canMutateOwnership(ownership)) {
              input.value = state.lines[index];
              return;
            }
            if (!beginTransaction()) {
              input.value = state.lines[index];
              return;
            }
            try {
              let previousLine: string | null = null;
              let previousFocus: HTMLElement | null = null;
              let candidateTray: TrayOwnership | null = null;
              try {
                previousLine = state.lines[index];
                const candidateLine = input.value;
                const candidateLines: [string, string, string] = [...state.lines];
                candidateLines[index] = candidateLine;
                const status = annotationStatus(state.annotations, candidateLines.join("\n"));
                previousFocus = document.activeElement as HTMLElement | null;
                const originalMode = currentOwnership;
                const originalGeneration = stateGeneration;
                const oldTray = trayOwnership;
                candidateTray = status.result === null ? null : prepareTray(status.result);
                if (candidateTray !== null) {
                  provisionallyAttach([{ slot: pendingSlot, node: candidateTray.host }]);
                }
                if (destroyed || currentOwnership !== originalMode || originalMode !== ownership
                    || stateGeneration !== originalGeneration
                    || (candidateTray !== null && (
                      provisionalTrayOwnership !== candidateTray || candidateTray.destroyed
                    ))) {
                  throw new Error("compose edit was retired before commit");
                }
                if (provisionalTrayOwnership === candidateTray) provisionalTrayOwnership = null;
                state.lines[index] = candidateLine;
                state.repairPending = status.pending;
                trayOwnership = candidateTray;
                stateGeneration += 1;
                if (candidateTray !== null) revealProvisional(candidateTray.host);
                cleanupTray(oldTray);
                if (previousFocus !== null && owned.contains(previousFocus)) {
                  try { previousFocus.focus({ preventScroll: true }); } catch { /* best-effort */ }
                }
              } catch {
                cleanupTray(candidateTray);
                try { input.value = previousLine ?? state.lines[index]; } catch { /* best-effort */ }
                if (previousFocus !== null && owned.contains(previousFocus)) {
                  try { previousFocus.focus({ preventScroll: true }); } catch { /* best-effort */ }
                }
              } finally {
                if (provisionalTrayOwnership === candidateTray) cleanupTray(candidateTray);
              }
            } finally {
              endTransaction();
            }
          }, ownership.cleanups);
          fieldset.append(input);
        });
        element.append(fieldset);
        ownership.fallbackFocus = "compose-line-1";
      } else if (mode === "annotate") {
        appendTextElement(document, element, "h2", "Annotate haiku");
        const poem = canonicalPoem("annotate-poem");
        element.append(poem);
        ownership.annotationElement = poem;
        const projection = projectAnnotations(poem, repair?.accepted ?? annotations);
        if (destroyed || provisionalModeOwnership !== ownership || ownership.destroyed) {
          try { projection.destroy(); } catch { /* collected by preparation failure is unavailable */ }
          throw new Error("annotation projection preparation was retired");
        }
        ownership.projection = projection;
        ownership.fallbackFocus = "annotate-poem";
      } else if (mode === "weave") {
        element.append(canonicalPoem("weave-poem"));
        radioGroup(
          "Theme", "data-editor-theme", "weave-theme", THEME_DEFINITIONS,
          themeAvailability, () => state.themeId, (id): void => { state.themeId = id; },
        );
        radioGroup(
          "Choreography", "data-editor-choreography", "weave-choreography",
          CHOREOGRAPHY_DEFINITIONS, choreographyAvailability,
          () => state.choreographyId, (id): void => { state.choreographyId = id; },
        );
        ownership.fallbackFocus = `weave-theme-${state.themeId}`;
      } else {
        appendTextElement(document, element, "h2", "Stage preview");
        const poem = canonicalPoem("stage-poem");
        const stageRoot = document.createElement("div");
        stageRoot.setAttribute("data-north-star-renderer-host", "");
        stageRoot.setAttribute("aria-hidden", "true");
        element.append(poem, stageRoot);
        ownership.fallbackFocus = "stage-poem";
        if (repair === null) {
          const plan = resolveScenePlan({
            seed: `north-star/editor/v1\u0000${canonicalText()}\u0000${JSON.stringify(annotations)}`,
            themeId: state.themeId,
            choreographyId: state.choreographyId,
          });
          const renderer = rendererBoundary.mount(stageRoot, plan);
          if (destroyed || provisionalModeOwnership !== ownership || ownership.destroyed) {
            try { renderer.destroy(); } catch { /* preparation is already retired */ }
            throw new Error("stage renderer preparation was retired");
          }
          ownership.renderer = renderer;
          ownership.scenePlan = plan;
        }
      }
      return ownership;
    } catch (error) {
      const cleanupErrors = cleanupMode(ownership);
      if (cleanupErrors.length > 0) {
        throw new AggregateError([error, ...cleanupErrors], `${mode} preparation failed`);
      }
      throw error;
    }
  }

  function commitRepairDecision(annotations: readonly AnnotationRange[]): void {
    if (!beginTransaction()) {
      throw new Error("another editor transaction is active");
    }
    try {
      const previousFocus = document.activeElement as HTMLElement | null;
      const candidateAnnotations = freezeRanges(annotations);
      const status = annotationStatus(candidateAnnotations);
      const originalMode = currentOwnership;
      const originalGeneration = stateGeneration;
      const oldTray = trayOwnership;
      let candidateMode: ModeOwnership | null = null;
      let candidateTray: TrayOwnership | null = null;
      try {
        candidateMode = prepareMode(state.mode, candidateAnnotations, status.result);
        candidateTray = status.result === null ? null : prepareTray(status.result);
        provisionallyAttach([
          { slot: modeSlot, node: candidateMode.element },
          ...(candidateTray === null ? [] : [{ slot: pendingSlot, node: candidateTray.host }]),
        ]);
        if (destroyed || currentOwnership !== originalMode
            || stateGeneration !== originalGeneration
            || provisionalModeOwnership !== candidateMode || candidateMode.destroyed
            || (candidateTray !== null && (
              provisionalTrayOwnership !== candidateTray || candidateTray.destroyed
            ))) {
          throw new Error("repair decision was retired before commit");
        }
        provisionalModeOwnership = null;
        if (provisionalTrayOwnership === candidateTray) provisionalTrayOwnership = null;
        currentOwnership = candidateMode;
        trayOwnership = candidateTray;
        state.annotations = candidateAnnotations;
        state.repairPending = status.pending;
        stateGeneration += 1;
        revealProvisional(candidateMode.element);
        if (candidateTray !== null) revealProvisional(candidateTray.host);
        cleanupMode(originalMode);
        cleanupTray(oldTray);
        bestEffortFocus(candidateMode);
      } catch (error) {
        cleanupMode(candidateMode);
        cleanupTray(candidateTray);
        if (previousFocus !== null && owned.contains(previousFocus)) {
          try { previousFocus.focus({ preventScroll: true }); } catch { /* best-effort */ }
        }
        throw error;
      } finally {
        if (provisionalModeOwnership === candidateMode) cleanupMode(candidateMode);
        if (provisionalTrayOwnership === candidateTray) cleanupTray(candidateTray);
      }
    } finally {
      endTransaction();
    }
  }

  const rememberCurrentFocus = (): void => {
    try {
      const active = document.activeElement;
      if (active === null || !owned.contains(active)) return;
      const key = active.getAttribute("data-editor-focus");
      if (key !== null) focusByMode.set(state.mode, key);
    } catch {
      // Focus metadata cannot own transaction success.
    }
  };

  const setMode = (nextMode: EditorMode): boolean => {
    if (destroyed || nextMode === state.mode
        || !(EDITOR_MODES as readonly string[]).includes(nextMode)
        || state.repairPending) return false;
    if (!beginTransaction()) return false;
    try {
      const original = currentOwnership;
      const originalGeneration = stateGeneration;
      let candidate: ModeOwnership | null = null;
      try {
        candidate = prepareMode(nextMode, state.annotations, null);
        provisionallyAttach([{ slot: modeSlot, node: candidate.element }]);
        let result: SessionSaveResult;
        try {
          result = options.session.save(saveContents(nextMode));
        } catch {
          return false;
        }
        if (result.status === "clock-error" || destroyed
            || provisionalModeOwnership !== candidate || candidate.destroyed
            || currentOwnership !== original || stateGeneration !== originalGeneration) {
          return false;
        }
        rememberCurrentFocus();
        commitModeOwnership(candidate, nextMode, true);
        try { options.onModeChanged?.(nextMode); } catch { /* committed mode remains authoritative */ }
        return true;
      } catch {
        return false;
      } finally {
        if (provisionalModeOwnership === candidate) cleanupMode(candidate);
        if (!destroyed && original !== null && currentOwnership === original
            && candidate?.destroyed !== false) {
          bestEffortFocus(original);
        }
      }
    } finally {
      endTransaction();
    }
  };

  if (modeNavigation !== null) {
    for (const mode of EDITOR_MODES) {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = mode[0]?.toUpperCase() + mode.slice(1);
      button.setAttribute("data-editor-mode", mode);
      button.setAttribute("aria-controls", `north-star-editor-${instanceId}-panel`);
      listen(button, "click", (): void => { setMode(mode); }, globalCleanups);
      modeNavigation.append(button);
      modeButtons.set(mode, button);
    }
  }

  const controller: NorthStarEditorController = Object.freeze({
    element: owned,
    get mode(): EditorMode { return state.mode; },
    get state(): CanonicalEditorState { return snapshot(); },
    get canonicalText(): string { return canonicalText(); },
    get scenePlan(): ScenePlan | null { return currentOwnership?.scenePlan ?? null; },
    setMode,
    captureSelection(id: string, selection?: EditorSelection | null): AnnotationCaptureResult {
      const annotationElement = currentOwnership?.annotationElement ?? null;
      if (destroyed || state.mode !== "annotate" || annotationElement === null
          || typeof id !== "string" || id.length === 0) {
        return Object.freeze({ status: "invalid-selection" });
      }
      if (!beginTransaction()) return Object.freeze({ status: "invalid-selection" });
      try {
        let previousFocus: HTMLElement | null = null;
        let candidateMode: ModeOwnership | null = null;
        let candidateTray: TrayOwnership | null = null;
        try {
          previousFocus = document.activeElement as HTMLElement | null;
          const originalMode = currentOwnership;
          const originalTray = trayOwnership;
          const originalGeneration = stateGeneration;
          const resolvedSelection = selection === undefined
            ? options.getSelection?.() ?? null
            : selection;
          if (destroyed || currentOwnership !== originalMode
              || stateGeneration !== originalGeneration) {
            return Object.freeze({ status: "invalid-selection" });
          }
          const offsets = selectionToCodePointOffsets(annotationElement, resolvedSelection);
          if (offsets === null) return Object.freeze({ status: "invalid-selection" });
          const range = freezeRange({ id, ...offsets });
          const candidateAnnotations = freezeRanges([...state.annotations, range]);
          const status = annotationStatus(candidateAnnotations);
          candidateMode = prepareMode("annotate", candidateAnnotations, status.result);
          candidateTray = status.result === null ? null : prepareTray(status.result);
          provisionallyAttach([
            { slot: modeSlot, node: candidateMode.element },
            ...(candidateTray === null ? [] : [{ slot: pendingSlot, node: candidateTray.host }]),
          ]);
          if (destroyed || currentOwnership !== originalMode
              || stateGeneration !== originalGeneration
              || provisionalModeOwnership !== candidateMode || candidateMode.destroyed
              || (candidateTray !== null && (
                provisionalTrayOwnership !== candidateTray || candidateTray.destroyed
              ))) {
            throw new Error("annotation capture was retired before commit");
          }
          provisionalModeOwnership = null;
          if (provisionalTrayOwnership === candidateTray) provisionalTrayOwnership = null;
          currentOwnership = candidateMode;
          trayOwnership = candidateTray;
          state.annotations = candidateAnnotations;
          state.repairPending = status.pending;
          stateGeneration += 1;
          revealProvisional(candidateMode.element);
          if (candidateTray !== null) revealProvisional(candidateTray.host);
          cleanupMode(originalMode);
          cleanupTray(originalTray);
          bestEffortFocus(candidateMode);
          return Object.freeze({ status: status.pending ? "repair-pending" : "accepted", range });
        } catch {
          cleanupMode(candidateMode);
          cleanupTray(candidateTray);
          if (previousFocus !== null && owned.contains(previousFocus)) {
            try { previousFocus.focus({ preventScroll: true }); } catch { /* best-effort */ }
          }
          return Object.freeze({ status: "invalid-selection" });
        } finally {
          if (provisionalModeOwnership === candidateMode) cleanupMode(candidateMode);
          if (provisionalTrayOwnership === candidateTray) cleanupTray(candidateTray);
        }
      } finally {
        endTransaction();
      }
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      const mode = currentOwnership;
      const tray = trayOwnership;
      const provisionalMode = provisionalModeOwnership;
      const provisionalTray = provisionalTrayOwnership;
      const cleanups = globalCleanups.splice(0);
      currentOwnership = null;
      trayOwnership = null;
      provisionalModeOwnership = null;
      provisionalTrayOwnership = null;
      const errors: unknown[] = [];
      errors.push(...cleanupMode(mode));
      if (provisionalMode !== mode) errors.push(...cleanupMode(provisionalMode));
      errors.push(...cleanupTray(tray));
      if (provisionalTray !== tray) errors.push(...cleanupTray(provisionalTray));
      for (const cleanup of cleanups) {
        try { cleanup(); } catch (error) { errors.push(error); }
      }
      try { pendingSlot.replaceChildren(); } catch (error) { errors.push(error); }
      try { modeSlot.replaceChildren(); } catch (error) { errors.push(error); }
      try { owned.remove(); } catch (error) { errors.push(error); }
      throwCleanupErrors(errors, "north-star editor cleanup failed");
    },
  });

  const initialFocus = document.activeElement as HTMLElement | null;
  let initialMode: ModeOwnership | null = null;
  let initialTray: TrayOwnership | null = null;
  try {
    const status = annotationStatus(state.annotations);
    state.repairPending = status.pending;
    initialMode = prepareMode(state.mode, state.annotations, status.result);
    initialTray = status.result === null ? null : prepareTray(status.result);
    modeSlot.append(initialMode.element);
    if (initialTray !== null) pendingSlot.append(initialTray.host);
    provisionalModeOwnership = null;
    if (provisionalTrayOwnership === initialTray) provisionalTrayOwnership = null;
    currentOwnership = initialMode;
    trayOwnership = initialTray;
    updateModeButtons();
    options.root.append(owned);
    if (initialFocus !== null && document.activeElement !== initialFocus) {
      try { initialFocus.focus({ preventScroll: true }); } catch { /* best-effort */ }
    }
  } catch (error) {
    destroyed = true;
    currentOwnership = null;
    trayOwnership = null;
    const errors = [...cleanupMode(initialMode), ...cleanupTray(initialTray)];
    for (const cleanup of globalCleanups.splice(0)) {
      try { cleanup(); } catch (cleanupError) { errors.push(cleanupError); }
    }
    try { owned.remove(); } catch (cleanupError) { errors.push(cleanupError); }
    if (errors.length > 0) {
      throw new AggregateError([error, ...errors], "north-star editor initialization failed");
    }
    throw error;
  }

  return controller;
};
